import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Lightweight, a11y-friendly pagination for admin tables.
 * - Controlled: parent owns `page` state.
 * - Page size selector + prev/next + compact page buttons.
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 15, 25, 50, 100],
  testId = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIdx = Math.min(safePage * pageSize, total);

  const go = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== safePage) onPageChange(next);
  };

  // Compact page range: current +/- 2 with ellipses
  const pages = [];
  const push = (v) => pages.push(v);
  const span = 1;
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (safePage - span > 2) push("…-left");
    for (let i = Math.max(2, safePage - span); i <= Math.min(totalPages - 1, safePage + span); i++) push(i);
    if (safePage + span < totalPages - 1) push("…-right");
    push(totalPages);
  }

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-2 py-3 text-xs"
      data-testid={testId}
    >
      <div className="text-muted-foreground">
        {total === 0 ? (
          <>No records</>
        ) : (
          <>Showing <b className="text-forest">{startIdx}–{endIdx}</b> of <b className="text-forest">{total}</b></>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {onPageSizeChange && (
          <label className="flex items-center gap-2 text-muted-foreground">
            <span>Rows</span>
            <select
              className="bg-cream border border-border rounded-full px-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-sage/50"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              data-testid={`${testId}-size`}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          onClick={() => go(safePage - 1)}
          disabled={safePage <= 1}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 hover:bg-forest hover:text-cream hover:border-forest transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground"
        >
          <CaretLeft size={12} weight="bold" /> Prev
        </button>
        <div className="flex items-center gap-1">
          {pages.map((p, i) =>
            typeof p === "number" ? (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                data-testid={`${testId}-page-${p}`}
                className={`min-w-[30px] h-[28px] rounded-full px-2 text-xs transition-all ${
                  p === safePage
                    ? "bg-forest text-cream"
                    : "border border-border hover:bg-sand"
                }`}
                aria-current={p === safePage ? "page" : undefined}
              >
                {p}
              </button>
            ) : (
              <span key={p + i} className="px-1 text-muted-foreground select-none">…</span>
            )
          )}
        </div>
        <button
          type="button"
          onClick={() => go(safePage + 1)}
          disabled={safePage >= totalPages}
          data-testid={`${testId}-next`}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 hover:bg-forest hover:text-cream hover:border-forest transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground"
        >
          Next <CaretRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
